import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';

/**
 * Publishes a --keyboard-inset-bottom CSS variable that tracks how much of
 * the layout viewport is being eaten by the virtual keyboard. Android
 * Chrome ignores `interactive-widget=resizes-content`, so iOS/Safari is
 * covered by the meta tag and Android is covered here.
 *
 * The component that owns the chat surface calls `attach()` from a DI
 * scope so the listeners are tied to a DestroyRef and clean themselves up.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardInsetService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  attach(destroyRef: DestroyRef): void {
    const win = this.document.defaultView;
    const vv = win?.visualViewport;
    if (!win || !vv) {
      return;
    }

    const apply = (): void => {
      const inset = Math.max(0, win.innerHeight - vv.height - vv.offsetTop);
      this.document.documentElement.style.setProperty(
        '--keyboard-inset-bottom',
        `${inset}px`,
      );
    };

    this.zone.runOutsideAngular(() => {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
    });
    apply();

    destroyRef.onDestroy(() => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      this.document.documentElement.style.removeProperty('--keyboard-inset-bottom');
    });
  }
}
